import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users,
  TrendingUp,
  DollarSign,
  Calendar,
  FileText,
  Plus,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  CreditCard,
  Download,
  ChevronRight,
  Sparkles,
  Heart,
  Edit
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { UserMenu } from '../components/UserMenu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (value) => {
  if (!value && value !== 0) return '$ 0';
  return `$ ${Math.round(value).toLocaleString('es-CO')}`;
};

const ESTADOS_REFERIDO = {
  registrado: { label: 'Registrado', color: 'bg-zinc-600', icon: Clock },
  pendiente_contacto: { label: 'Pendiente contacto', color: 'bg-amber-600', icon: Clock },
  contactado: { label: 'Contactado', color: 'bg-blue-600', icon: CheckCircle },
  cotizado: { label: 'Cotizado', color: 'bg-purple-600', icon: FileText },
  venta_realizada: { label: 'Venta realizada', color: 'bg-green-600', icon: CheckCircle },
  no_fue_venta: { label: 'No fue venta', color: 'bg-red-600', icon: XCircle },
  listo_cobro: { label: 'Listo para cobro', color: 'bg-[#D4AF37]', icon: DollarSign },
  pagado: { label: 'Pagado', color: 'bg-emerald-600', icon: CheckCircle }
};

const PanelAliado = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [panelData, setPanelData] = useState(null);
  const [error, setError] = useState(null);

  const fetchPanelData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError({ type: 'auth', message: 'No hay sesión activa. Por favor inicia sesión.' });
        return;
      }
      
      console.log('[PanelAliado] Cargando panel para usuario:', user?.email, 'rol:', user?.role);
      
      const response = await axios.get(`${API_URL}/api/aliados/mi-panel`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[PanelAliado] Datos recibidos:', response.data?.aliado?.id_aliado);
      setPanelData(response.data);
    } catch (error) {
      console.error('[PanelAliado] Error:', error.response?.status, error.response?.data);
      
      if (error.response?.status === 403) {
        setError({ 
          type: 'forbidden', 
          message: 'Este panel es exclusivo para usuarios con rol Aliado.',
          detail: error.response?.data?.detail 
        });
      } else if (error.response?.status === 401) {
        setError({ 
          type: 'auth', 
          message: 'Tu sesión ha expirado. Por favor inicia sesión nuevamente.' 
        });
      } else {
        setError({ 
          type: 'error', 
          message: 'Error al cargar el panel. Intenta de nuevo.',
          detail: error.response?.data?.detail || error.message
        });
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPanelData();
  }, [fetchPanelData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto mb-4"></div>
          <p className="text-zinc-400">Cargando tu panel de aliado...</p>
          <p className="text-zinc-500 text-sm mt-1">Verificando permisos y datos...</p>
        </div>
      </div>
    );
  }

  // Mostrar pantalla de error específica
  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <h2 className="text-xl font-display mb-2 text-white">{error.message}</h2>
          {error.detail && (
            <p className="text-zinc-400 text-sm mb-4">{error.detail}</p>
          )}
          <div className="flex flex-col gap-3 mt-6">
            {error.type === 'auth' && (
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-3 bg-[#D4AF37] text-black rounded-lg font-medium hover:bg-[#B8962E] transition-colors"
              >
                Iniciar Sesión
              </button>
            )}
            {error.type === 'forbidden' && (
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 bg-zinc-700 text-white rounded-lg font-medium hover:bg-zinc-600 transition-colors"
              >
                Ir al Dashboard
              </button>
            )}
            <button
              onClick={fetchPanelData}
              className="px-6 py-3 border border-zinc-600 text-zinc-300 rounded-lg font-medium hover:bg-zinc-800 transition-colors"
            >
              Reintentar
            </button>
          </div>
          {user && (
            <p className="text-zinc-600 text-xs mt-6">
              Usuario: {user.email} | Rol: {user.role}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!panelData || !panelData.aliado) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <h2 className="text-xl font-display mb-2 text-white">Configurando tu perfil de aliado...</h2>
          <p className="text-zinc-400 mb-4">
            Tu cuenta está aprobada pero el perfil de aliado aún se está creando.
          </p>
          <button
            onClick={fetchPanelData}
            className="px-6 py-3 bg-[#D4AF37] text-black rounded-lg font-medium hover:bg-[#B8962E] transition-colors"
          >
            Reintentar
          </button>
          {user && (
            <p className="text-zinc-600 text-xs mt-6">
              Usuario: {user.email} | Rol: {user.role}
            </p>
          )}
        </div>
      </div>
    );
  }

  const { aliado, estadisticas, datos_cobro_completos, campos_cobro_faltantes, referidos_recientes } = panelData;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Barra superior con menú de usuario */}
      <div className="sticky top-0 z-50 bg-zinc-950/90 backdrop-blur-lg border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-[#D4AF37]" />
              <span className="font-display text-lg">ATELIER — JPM</span>
            </div>
            <UserMenu variant="dark" />
          </div>
        </div>
      </div>

      {/* Hero Section con imagen de fondo */}
      <div className="relative overflow-hidden">
        {/* Imagen de fondo */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: 'url(/images/novio-mascota.png)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/50 via-zinc-950/80 to-zinc-950" />
        
        {/* Contenido del Hero */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-sm font-medium tracking-wider uppercase">Panel de Aliado</span>
              </div>
              <h1 className="font-display text-3xl sm:text-4xl mb-2">
                Bienvenido, <span className="text-[#D4AF37]">{aliado.nombre}</span>
              </h1>
              <p className="text-zinc-400">Juan Pablo Murillo Novios - Programa de Referidos</p>
            </div>
            
            <button
              onClick={() => navigate(`/aliados/${aliado.id}/referidos/nuevo`)}
              className="flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-3 rounded-lg hover:bg-[#C49F27] transition-all hover:scale-105 font-medium"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Nuevo Referido</span>
            </button>
          </div>

          {/* Alerta de datos bancarios incompletos */}
          {!datos_cobro_completos && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-8 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-200 font-medium">Datos bancarios incompletos</p>
                <p className="text-amber-200/70 text-sm">
                  Para poder generar cuentas de cobro, completa tus datos bancarios. 
                  Campos faltantes: {campos_cobro_faltantes?.join(', ') || 'varios'}
                </p>
                <button
                  onClick={() => navigate(`/aliados/${aliado.id}/editar`)}
                  className="mt-2 text-[#D4AF37] text-sm font-medium hover:underline flex items-center gap-1"
                >
                  Completar datos <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Referidos */}
            <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2.5 bg-[#D4AF37]/20 rounded-lg">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <span className="text-zinc-400 text-sm">Total Referidos</span>
              </div>
              <p className="text-3xl font-display">{estadisticas.total_referidos}</p>
              <div className="flex gap-2 mt-2 text-xs">
                <span className="text-green-400">{estadisticas.referidos_vendidos} vendidos</span>
                <span className="text-zinc-600">|</span>
                <span className="text-amber-400">{estadisticas.referidos_pendientes} pendientes</span>
              </div>
            </div>

            {/* Comisiones Pendientes */}
            <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2.5 bg-amber-500/20 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-500" />
                </div>
                <span className="text-zinc-400 text-sm">Por Cobrar</span>
              </div>
              <p className="text-2xl font-display text-amber-400">{formatPrice(estadisticas.comisiones_pendientes)}</p>
              <p className="text-xs text-zinc-500 mt-2">Comisiones pendientes de pago</p>
            </div>

            {/* Comisiones Pagadas */}
            <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2.5 bg-green-500/20 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <span className="text-zinc-400 text-sm">Cobrado</span>
              </div>
              <p className="text-2xl font-display text-green-400">{formatPrice(estadisticas.comisiones_pagadas)}</p>
              <p className="text-xs text-zinc-500 mt-2">Total recibido hasta la fecha</p>
            </div>

            {/* Weddings */}
            <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2.5 bg-pink-500/20 rounded-lg">
                  <Heart className="w-5 h-5 text-pink-500" />
                </div>
                <span className="text-zinc-400 text-sm">Weddings</span>
              </div>
              <p className="text-3xl font-display">{estadisticas.total_weddings}</p>
              <p className="text-xs text-zinc-500 mt-2">Eventos registrados</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido Principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 -mt-4">
        <div className="grid lg:grid-cols-3 gap-6">
          
          {/* Columna Principal - Referidos Recientes */}
          <div className="lg:col-span-2 space-y-6">
            {/* Referidos Recientes */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="p-5 border-b border-zinc-800 flex justify-between items-center">
                <h2 className="font-display text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                  Referidos Recientes
                </h2>
                <button
                  onClick={() => navigate(`/aliados/${aliado.id}/referidos`)}
                  className="text-[#D4AF37] text-sm hover:underline flex items-center gap-1"
                >
                  Ver todos <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              
              {referidos_recientes && referidos_recientes.length > 0 ? (
                <div className="divide-y divide-zinc-800">
                  {referidos_recientes.slice(0, 5).map((referido) => {
                    const estadoInfo = ESTADOS_REFERIDO[referido.estado] || ESTADOS_REFERIDO.registrado;
                    const EstadoIcon = estadoInfo.icon;
                    
                    return (
                      <div key={referido.id} className="p-4 hover:bg-zinc-800/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${estadoInfo.color}/20`}>
                              <EstadoIcon className={`w-4 h-4 ${estadoInfo.color.replace('bg-', 'text-')}`} />
                            </div>
                            <div>
                              <p className="font-medium">{referido.nombre}</p>
                              <p className="text-sm text-zinc-400">
                                {referido.tipo_referido === 'novio' ? 'Novio' : 
                                 referido.tipo_referido === 'invitado' ? 'Invitado' : 
                                 referido.tipo_referido === 'mascota' ? 'Mascota' : referido.tipo_referido}
                                {referido.fecha_evento && ` - ${new Date(referido.fecha_evento).toLocaleDateString('es-CO')}`}
                              </p>
                              {/* Desglose IVA */}
                              {referido.valor_cotizacion > 0 && (
                                <p className="text-xs text-zinc-500 mt-1">
                                  Base sin IVA: {formatPrice(referido.valor_base_sin_iva || Math.round(referido.valor_cotizacion / 1.19))}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            {referido.valor_comision > 0 && (
                              <>
                                <p className="text-[#D4AF37] font-medium">{formatPrice(referido.valor_comision)}</p>
                                <p className="text-xs text-zinc-500">
                                  {referido.porcentaje_comision}% s/base
                                </p>
                              </>
                            )}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${estadoInfo.color} text-white`}>
                              {estadoInfo.label}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Users className="w-12 h-12 mx-auto text-zinc-700 mb-3" />
                  <p className="text-zinc-400 mb-4">Aún no tienes referidos registrados</p>
                  <button
                    onClick={() => navigate(`/aliados/${aliado.id}/referidos/nuevo`)}
                    className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-lg hover:bg-[#C49F27] transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Agregar primer referido
                  </button>
                </div>
              )}
            </div>

            {/* Tabla de Comisiones por Tipo */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
              <h2 className="font-display text-lg mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                Estructura de Comisiones
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-3 px-4 text-zinc-400 font-medium">Tipo de Referido</th>
                      <th className="text-center py-3 px-4 text-zinc-400 font-medium">Porcentaje</th>
                      <th className="text-right py-3 px-4 text-zinc-400 font-medium">Ejemplo ($5M)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    <tr>
                      <td className="py-3 px-4">Novio</td>
                      <td className="py-3 px-4 text-center text-[#D4AF37] font-medium">5%</td>
                      <td className="py-3 px-4 text-right text-green-400">$ 250.000</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Invitado</td>
                      <td className="py-3 px-4 text-center text-[#D4AF37] font-medium">3%</td>
                      <td className="py-3 px-4 text-right text-green-400">$ 150.000</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Mascota</td>
                      <td className="py-3 px-4 text-center text-[#D4AF37] font-medium">3%</td>
                      <td className="py-3 px-4 text-right text-green-400">$ 150.000</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Corte Completa</td>
                      <td className="py-3 px-4 text-center text-[#D4AF37] font-medium">5%</td>
                      <td className="py-3 px-4 text-right text-green-400">$ 250.000</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Columna Lateral */}
          <div className="space-y-6">
            {/* Acciones Rápidas */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
              <h2 className="font-display text-lg mb-4">Acciones Rápidas</h2>
              <div className="space-y-3">
                <button
                  onClick={() => navigate(`/aliados/${aliado.id}/referidos/nuevo`)}
                  className="w-full flex items-center gap-3 p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg hover:bg-[#D4AF37]/20 transition-colors text-left"
                >
                  <Plus className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <p className="font-medium text-[#D4AF37]">Nuevo Referido</p>
                    <p className="text-xs text-zinc-400">Registra un nuevo cliente referido</p>
                  </div>
                </button>
                
                <button
                  onClick={() => navigate(`/aliados/${aliado.id}/referidos`)}
                  className="w-full flex items-center gap-3 p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg hover:bg-zinc-800 transition-colors text-left"
                >
                  <Eye className="w-5 h-5 text-zinc-400" />
                  <div>
                    <p className="font-medium">Ver Referidos</p>
                    <p className="text-xs text-zinc-400">Consulta todos tus referidos</p>
                  </div>
                </button>
                
                <button
                  onClick={() => navigate(`/aliados/${aliado.id}/editar`)}
                  className="w-full flex items-center gap-3 p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg hover:bg-zinc-800 transition-colors text-left"
                >
                  <CreditCard className="w-5 h-5 text-zinc-400" />
                  <div>
                    <p className="font-medium">Datos Bancarios</p>
                    <p className="text-xs text-zinc-400">Actualiza tu información de pago</p>
                  </div>
                </button>
                
                {/* Cuentas de Cobro - Solo para proveedores y prestadores de servicio */}
                {['proveedor', 'prestador_servicio', 'prestador de servicio'].includes(aliado.tipo_aliado?.toLowerCase()) && (
                  <button
                    onClick={() => navigate('/cuentas-cobro')}
                    className="w-full flex items-center gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg hover:bg-amber-500/20 transition-colors text-left"
                  >
                    <FileText className="w-5 h-5 text-amber-500" />
                    <div>
                      <p className="font-medium text-amber-400">Cuentas de Cobro</p>
                      <p className="text-xs text-zinc-400">Crea y consulta tus cuentas de cobro</p>
                    </div>
                  </button>
                )}
              </div>
            </div>

            {/* Información del Aliado */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-display text-lg">Tu Información</h2>
                <button
                  onClick={() => navigate(`/aliados/${aliado.id}/editar`)}
                  className="text-[#D4AF37] text-xs font-medium hover:underline flex items-center gap-1"
                >
                  <Edit className="w-3 h-3" />
                  Editar
                </button>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-zinc-400 text-sm">Tipo</span>
                  <span className="text-sm font-medium capitalize">{aliado.tipo_aliado?.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400 text-sm">Documento</span>
                  <span className="text-sm font-medium">{aliado.numero_documento}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400 text-sm">Teléfono</span>
                  <span className="text-sm font-medium">{aliado.telefono}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400 text-sm">Correo</span>
                  <span className="text-sm font-medium truncate ml-2">{aliado.correo}</span>
                </div>
                {aliado.ciudad && (
                  <div className="flex justify-between">
                    <span className="text-zinc-400 text-sm">Ciudad</span>
                    <span className="text-sm font-medium">{aliado.ciudad}</span>
                  </div>
                )}
                {aliado.direccion && (
                  <div className="flex justify-between">
                    <span className="text-zinc-400 text-sm">Dirección</span>
                    <span className="text-sm font-medium truncate ml-2">{aliado.direccion}</span>
                  </div>
                )}
                {aliado.whatsapp && (
                  <div className="flex justify-between">
                    <span className="text-zinc-400 text-sm">WhatsApp</span>
                    <span className="text-sm font-medium">{aliado.whatsapp}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-zinc-800">
                  <span className="text-zinc-400 text-sm">Estado</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    aliado.estado === 'activo' ? 'bg-green-500/20 text-green-400' :
                    aliado.estado === 'inactivo' ? 'bg-zinc-500/20 text-zinc-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {aliado.estado?.charAt(0).toUpperCase() + aliado.estado?.slice(1)}
                  </span>
                </div>
              </div>
            </div>

            {/* Datos Bancarios */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-display text-lg">Datos Bancarios</h2>
                {datos_cobro_completos && (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                )}
              </div>
              
              {aliado.datos_cobro && aliado.datos_cobro.banco ? (
                <div className="space-y-2 text-sm">
                  <p><span className="text-zinc-400">Titular:</span> {aliado.datos_cobro.titular_cuenta}</p>
                  <p><span className="text-zinc-400">Banco:</span> {aliado.datos_cobro.banco}</p>
                  <p><span className="text-zinc-400">Cuenta:</span> {aliado.datos_cobro.tipo_cuenta} - ****{aliado.datos_cobro.numero_cuenta?.slice(-4)}</p>
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">Sin datos bancarios configurados</p>
              )}
              
              <button
                onClick={() => navigate(`/aliados/${aliado.id}/editar`)}
                className="w-full mt-4 text-[#D4AF37] text-sm font-medium hover:underline"
              >
                {datos_cobro_completos ? 'Actualizar datos' : 'Completar datos'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Premium */}
      <div className="border-t border-zinc-800 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <p className="text-[#D4AF37] font-display text-lg mb-2">Juan Pablo Murillo Novios</p>
            <p className="text-zinc-500 text-sm">Programa de Aliados y Referidos</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PanelAliado;
